import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Row, Col, Card, Carousel, Button, Select, message } from "antd";
import { LeftOutlined, RightOutlined, PlusOutlined, MinusOutlined } from "@ant-design/icons";
import { useCart } from "../../Cart/CartContext";

const { Meta } = Card;
const { Option } = Select;

const ProductCarousel = ({ data }) => {
  const { t, i18n } = useTranslation();
  const currentLanguage = i18n.language;

  const [selectedOption, setSelectedOption] = useState(() =>
    data.map((product) => product.options[0] || null),
  );

  const [counters, setCounters] = useState(data.map(() => 1));
  const [cart, setCart] = useState([]);

  const { updateCartCount } = useCart();

  useEffect(() => {
    const savedCart = localStorage.getItem("cart");
    if (savedCart) {
      const parsedCart = JSON.parse(savedCart);
      setCart(parsedCart);

      setCounters(
        data.map((item) => {
          const cartItem = parsedCart.find((ci) => ci.id === item.id);
          return cartItem ? cartItem.quantity : 1;
        }),
      );

      setSelectedOption(
        data.map((item) => {
          const cartItem = parsedCart.find((ci) => ci.id === item.id);
          return cartItem?.selectedOption || item.options[0] || null;
        }),
      );
    }
  }, [data]);

  useEffect(() => {
    localStorage.setItem("cart", JSON.stringify(cart));
    updateCartCount();
  }, [cart, updateCartCount]);

  const getAvailableQuantity = (product, selectedOpt) => {
    if (!selectedOpt) return 0;
    return product.options.find((opt) => opt.id === selectedOpt.id)?.availableQuantity || 0;
  };

  const handleCounterChange = (index, increment) => {
    const maxQty = getAvailableQuantity(data[index], selectedOption[index]);
    setCounters((prev) =>
      prev.map((val, i) => (i === index ? Math.min(Math.max(1, val + increment), maxQty) : val)),
    );
  };

  const handleAddOrUpdateCart = (index) => {
    const maxQty = getAvailableQuantity(data[index], selectedOption[index]);

    if (counters[index] > maxQty) {
      message.warning(t("home.products.notEnoughStock"));
      return;
    }

    const selectedItem = {
      ...data[index],
      quantity: counters[index],
      selectedOption: selectedOption[index], // now just a single option
    };

    setCart((prevCart) => {
      const existingIndex = prevCart.findIndex((item) => item.id === selectedItem.id);
      if (existingIndex !== -1) {
        const updated = [...prevCart];
        updated[existingIndex] = selectedItem;
        return updated;
      }
      return [...prevCart, selectedItem];
    });
  };

  const customPrevArrow = <LeftOutlined />;
  const customNextArrow = <RightOutlined />;

  return (
    <Carousel
      arrows
      prevArrow={customPrevArrow}
      nextArrow={customNextArrow}
      dots={false}
      autoplay={false}
      slidesToShow={Math.min(data.length, 3)}
      slidesToScroll={1}
    >
      {data.map((product, index) => {
        const name = product["name" + (currentLanguage === "en" ? "En" : "Ar")];
        const description = product["description" + (currentLanguage === "en" ? "En" : "Ar")];
        const isInCart = cart.some((item) => item.id === product.id);

        const uniqueOptions = [
          ...new Map(
            product.options.map((opt) => [
              currentLanguage === "en" ? opt.valueEn : opt.valueAr,
              opt,
            ]),
          ).values(),
        ];

        const optionCategoryName =
          currentLanguage === "en"
            ? product.options[0]?.optionCategory?.nameEn
            : product.options[0]?.optionCategory?.nameAr || "";

        const selectedOpt = selectedOption[index];
        const selectedValue =
          selectedOpt && currentLanguage === "en" ? selectedOpt.valueEn : selectedOpt?.valueAr;

        const maxQty = getAvailableQuantity(product, selectedOpt);

        return (
          <div className="w-auto px-2 px-md-5" key={product.id}>
            <Card
              style={{ width: 300 }}
              cover={<img alt={name} src={product.picture} />}
              actions={[
                <div
                  key="counter"
                  style={{ display: "flex", alignItems: "center", justifyContent: "center" }}
                >
                  <Button
                    onClick={() => handleCounterChange(index, -1)}
                    disabled={counters[index] === 1}
                    icon={<MinusOutlined />}
                  />
                  <span style={{ margin: "0 10px" }}>{counters[index]}</span>
                  <Button
                    onClick={() => handleCounterChange(index, 1)}
                    icon={<PlusOutlined />}
                    disabled={counters[index] >= maxQty}
                  />
                </div>,
                <Button key="add" type="primary" onClick={() => handleAddOrUpdateCart(index)}>
                  {isInCart
                    ? t("home.products.updateCartButton")
                    : t("home.products.addToCartButton")}
                </Button>,
              ]}
            >
              <Meta title={name} description={description} />

              {optionCategoryName && (
                <div style={{ marginTop: 10 }}>
                  <span style={{ fontWeight: "bold" }}>{optionCategoryName}:</span>
                  <Select
                    style={{ width: "100%", marginTop: 5 }}
                    value={selectedValue}
                    onChange={(value) => {
                      const newOption = uniqueOptions.find((opt) =>
                        currentLanguage === "en" ? opt.valueEn === value : opt.valueAr === value,
                      );
                      if (!newOption) return;
                      const updatedSelections = [...selectedOption];
                      updatedSelections[index] = newOption;
                      setSelectedOption(updatedSelections);
                      setCounters((prev) => prev.map((val, i) => (i === index ? 1 : val)));
                    }}
                  >
                    {uniqueOptions.map((opt) => {
                      const value = currentLanguage === "en" ? opt.valueEn : opt.valueAr;
                      return (
                        <Option key={value} value={value}>
                          {value}
                        </Option>
                      );
                    })}
                  </Select>
                </div>
              )}

              <Row>
                <Col span={24} style={{ color: "rgba(0, 0, 0, 0.45)", marginTop: 8 }}>
                  {t("home.products.price")}:&nbsp;
                  <span>{product.price.toFixed(2)} KD</span>
                </Col>
              </Row>
            </Card>
          </div>
        );
      })}
    </Carousel>
  );
};

export default ProductCarousel;
